// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, CheckCircleIcon, ClipboardDocumentListIcon, ClockIcon, EnvelopeIcon, PlusIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import Header from "../../components/TitleAndSubtitle";
import { ActivityBarChart, StatusDoughnutChart } from "../../components/dashboard/DashboardCharts";
import PersonalSupportSummary from "../../components/dashboard/PersonalSupportSummary";
import { URL } from "../../service/Service";
import { formatExpirationDate, isFormExpired } from "../../utils/formExpiration";

const authHeaders = () => ({
  Accept: "application/json",
  Authorization: `Bearer ${sessionStorage.getItem("jwt") || ""}`,
});
const asArray = (value) => (Array.isArray(value) ? value : Object.values(value || {}));
const money = (value) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(value || 0));

export default function CompanyDashboard() {
  const [forms, setForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [period, setPeriod] = useState(30);
  const [financial, setFinancial] = useState({ trend: [] });
  const [financialError, setFinancialError] = useState("");
  const [financialLoading, setFinancialLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([fetch(`${URL}/api/forms`, { headers: authHeaders() }).then((response) => (response.ok ? response.json() : [])), fetch(`${URL}/api/users/company`, { headers: authHeaders() }).then((response) => (response.ok ? response.json() : []))])
      .then(async ([formData, userData]) => {
        const companyForms = asArray(formData).filter(Boolean);
        const formStats = await Promise.all(
          companyForms.map(async (form) => {
            try {
              const response = await fetch(`${URL}/api/forms/${form.id}/replies?page=0&size=1`, { headers: authHeaders() });
              const data = response.ok ? await response.json() : {};
              return [
                form.id,
                {
                  total: Number(data.totalCount || 0),
                  paid: Number(data.paidCount || 0),
                  pending: Number(data.pendingCount || 0),
                },
              ];
            } catch (_error) {
              return [form.id, { total: 0, paid: 0, pending: 0 }];
            }
          }),
        );
        if (!active) return;
        setForms(companyForms);
        setUsers(asArray(userData));
        setStats(Object.fromEntries(formStats));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setFinancialError("");
    setFinancialLoading(true);
    fetch(`${URL}/api/dashboard/financial-metrics?period=${period}`, {
      headers: authHeaders(),
    })
      .then(async (response) => {
        if (!response.ok) {
          const detail = await response.text();
          throw new Error(detail || `Erro ${response.status}`);
        }
        return response.json();
      })
      .then((data) => active && setFinancial(data))
      .catch(() => {
        if (!active) return;
        setFinancial({ trend: [] });
        setFinancialError("Não foi possível carregar as métricas. Tenta atualizar a página.");
      })
      .finally(() => active && setFinancialLoading(false));
    return () => {
      active = false;
    };
  }, [period]);

  const liveForms = useMemo(() => forms.filter((form) => !form.deleted), [forms]);
  const activeForms = liveForms.filter((form) => form.status !== "PAUSED" && !isFormExpired(form)).length;
  const totalReplies = Number(financial.totalReplies || 0);
  const paid = Number(financial.paidCount || 0);
  const pending = Number(financial.pendingCount || 0);
  const periodDescription = period === 0 ? "Em todo o período" : `Nos últimos ${period} dias`;
  const recentForms = liveForms.slice(0, 5);
  const cards = [
    ["Formulários ativos", activeForms, `${liveForms.length} no total`, ClipboardDocumentListIcon, "bg-emerald-50 text-emerald-700", loading],
    ["Respostas", totalReplies, periodDescription, EnvelopeIcon, "bg-violet-50 text-violet-700", financialLoading],
    ["Recebido", money(financial.paidAmount), `${paid} pagamentos concluídos · ${periodDescription.toLowerCase()}`, CheckCircleIcon, "bg-sky-50 text-sky-700", financialLoading],
    ["Valor pendente", money(financial.pendingAmount), pending ? `${pending} pagamentos por concluir · ${periodDescription.toLowerCase()}` : `Tudo regularizado · ${periodDescription.toLowerCase()}`, ClockIcon, pending ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-600", financialLoading],
  ];

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 via-white to-emerald-50/50 px-4 py-7 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <Header
          eyebrow="Formulários"
          title="Visão geral dos formulários"
          description="Acompanhe formulários, respostas e pagamentos da sua conta."
          actions={
            <>
              <select value={period} onChange={(event) => setPeriod(Number(event.target.value))} className="min-h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm" aria-label="Período das métricas">
                <option value={7}>7 dias</option>
                <option value={30}>30 dias</option>
                <option value={90}>90 dias</option>
                <option value={0}>Todo o período</option>
              </select>
              <Link to="/company/users" className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm">
                <UserGroupIcon className="h-5 w-5" />
                Gerir equipa
              </Link>
              <Link to="/forms" className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-green-cl px-4 py-2.5 text-sm font-bold text-white shadow-sm">
                <PlusIcon className="h-5 w-5" />
                Criar formulário
              </Link>
            </>
          }
        />
        {financialError && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-800" role="alert">
            {financialError}
          </div>
        )}
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map(([label, value, detail, Icon, tone, cardLoading]) => (
            <article key={label} className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
                  <p className="mt-3 text-3xl font-extrabold text-slate-950" aria-live="polite">
                    {cardLoading ? "—" : value}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">{cardLoading ? "A atualizar…" : detail}</p>
                </div>
                <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>
                  <Icon className="h-5 w-5" />
                </span>
              </div>
            </article>
          ))}
        </section>
        <section className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_0.85fr]">
          <article className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-950">Volume recebido</h2>
            <p className="mb-5 mt-1 text-sm text-slate-500">Montantes pagos por dia no período selecionado.</p>
            <ActivityBarChart labels={(financial.trend || []).map((point) => point.date.slice(5))} values={(financial.trend || []).map((point) => point.paidAmount)} label="Recebido (€)" />
          </article>
          <article className="rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-950">Estado dos pagamentos</h2>
            <p className="mb-5 mt-1 text-sm text-slate-500">Concluídos e ainda pendentes.</p>
            <StatusDoughnutChart labels={["Concluídos", "Pendentes", "Sem pagamento"]} values={[paid, pending, Math.max(0, totalReplies - paid - pending)]} />
          </article>
        </section>
        <section className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Recebido</p>
            <strong className="mt-2 block text-xl text-slate-950" aria-live="polite">
              {financialLoading ? "—" : money(financial.paidAmount)}
            </strong>
            <span className="mt-1 block text-xs text-slate-500">{financialLoading ? "A atualizar…" : periodDescription}</span>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Reembolsado</p>
            <strong className="mt-2 block text-xl text-rose-700" aria-live="polite">
              {financialLoading ? "—" : money(financial.refundedAmount)}
            </strong>
            <span className="mt-1 block text-xs text-slate-500">{financialLoading ? "A atualizar…" : `${money(financial.pendingRefundAmount)} solicitado · ${periodDescription.toLowerCase()}`}</span>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pagamento médio</p>
            <strong className="mt-2 block text-xl text-slate-950" aria-live="polite">
              {financialLoading ? "—" : money(financial.averagePaidAmount)}
            </strong>
            <span className="mt-1 block text-xs text-slate-500">{financialLoading ? "A atualizar…" : periodDescription}</span>
          </div>
        </section>
        <section className="mt-6 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
            <div>
              <h2 className="text-xl font-extrabold text-slate-950">Formulários recentes</h2>
              <p className="mt-1 text-sm text-slate-500">Estado e atividade da empresa.</p>
            </div>
            <Link to="/forms" className="text-sm font-bold text-emerald-800">
              Ver todos
            </Link>
          </div>
          {recentForms.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-6 py-3">Formulário</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Respostas</th>
                    <th className="px-4 py-3">Pagos</th>
                    <th className="px-6 py-3 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentForms.map((form) => (
                    <tr key={form.id}>
                      <td className="px-6 py-4">
                        <span className="block font-bold text-slate-900">{form.name || "Formulário sem nome"}</span>
                        {form.expirationDate && <span className={`mt-1 block text-xs font-semibold ${isFormExpired(form) ? "text-red-700" : "text-slate-500"}`}>Disponível até {formatExpirationDate(form.expirationDate)}</span>}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${isFormExpired(form) ? "bg-red-50 text-red-700" : form.status === "PAUSED" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{isFormExpired(form) ? "Expirado" : form.status === "PAUSED" ? "Em pausa" : "Ativo"}</span>
                      </td>
                      <td className="px-4 py-4 text-slate-600">{stats[form.id]?.total || 0}</td>
                      <td className="px-4 py-4 text-slate-600">{stats[form.id]?.paid || 0}</td>
                      <td className="px-6 py-4 text-right">
                        <Link to={`/forms?form=${encodeURIComponent(form.id)}`} className="inline-flex items-center gap-1 font-bold text-emerald-800">
                          Abrir <ArrowRightIcon className="h-4 w-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-sm text-slate-500">Crie o primeiro formulário para começar.</div>
          )}
        </section>
        <PersonalSupportSummary />
        {!loading && users.length === 0 && (
          <div className="mt-6 flex justify-between rounded-2xl border border-sky-100 bg-sky-50 px-5 py-4 text-sm text-sky-900">
            <span>A equipa ainda não tem colaboradores com acesso.</span>
            <Link to="/company/users" className="font-bold">
              Adicionar membro →
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
